import type { NextPage } from "next";
import { useRouter } from "next/router";
import { useEffect } from "react";
import { useOptionalUser } from "../src/useUser";
import { FaGithub, FaGoogle, FaKey } from "react-icons/fa";
import ApertureAuthLayout from "../components/ApertureAuthLayout";
import Button from "../components/Button";
import LoadingPage from "../components/LoadingPage";
import { HiOutlineQuestionMarkCircle } from "react-icons/hi";
import { useSegmentCallback } from "../src/segment";
import { useProduct, useProductParam } from "../src/useProductParam";
import PlausibleProvider from "next-plausible";

const Home: NextPage = () => {
  const { user, isLoading } = useOptionalUser();
  const router = useRouter();

  const onGithubClick = useSegmentCallback("Index Github Log In Click");
  const onGoogleClick = useSegmentCallback("Index Google Log In Click");
  const onSSOClick = useSegmentCallback("Index SSO Log In Click");
  const onDocsClick = useSegmentCallback("Index Documentation Click");

  const product = useProduct();
  const productParam = useProductParam();
  const isAperture = product === "aperture";

  useEffect(() => {
    if (user) {
      // Redirect to dashboard view if user is logged in
      router.replace(`/agent-app/dashboard${window.location.search}`);
    }
  }, [user, router]);

  if (isLoading || user) {
    return <LoadingPage />;
  }

  if (isAperture) {
    return (
      <PlausibleProvider
        domain="osohq.com,ui.osohq.com"
        trackOutboundLinks={true}
      >
        <ApertureAuthLayout showDocs showUserAgreement>
          <Button
            block
            theme="google"
            loaderOnClick
            icon={<FaGoogle />}
            href={`/web/oauth/gsuite/login${productParam}`}
            onClick={onGoogleClick}
          >
            Sign in with Google
          </Button>
          <Button
            block
            loaderOnClick
            icon={<FaGithub className="h-[1em] w-[1em]" />}
            href={`/web/oauth/github/login${productParam}`}
            onClick={onGithubClick}
          >
            Sign in with Github
          </Button>
        </ApertureAuthLayout>
      </PlausibleProvider>
    );
  }

  // Default: centered single-column login (unchanged)
  return (
    <PlausibleProvider
      domain="osohq.com,ui.osohq.com"
      trackOutboundLinks={true}
    >
      <div className="fixed inset-0 flex items-center justify-center">
        <div className="flex flex-col items-center max-w-[350px]">
          <a href="https://www.osohq.com">
            <img
              src="/logo-purple-circle.svg"
              alt="Oso Logo"
              className="rounded-full shadow-xl grow-0 hover:scale-105 transition-transform"
            />
          </a>
          <div className="mt-10" />
          <div className="font-space text-gray-800 text-2xl text-center">
            Oso for{" "}
            <span className="relative">
              <span>A</span>
              <span className="relative">
                <span className="animate-fade-word-1">pps</span>
                <span className="absolute left-0 top-0 animate-fade-word-2">
                  gents
                </span>
              </span>
            </span>
          </div>
          <div className="mt-10" />
          <div className="flex flex-col space-y-4 w-full justify-center">
            <Button
              block
              theme="google"
              loaderOnClick
              icon={<FaGoogle />}
              href={`/web/oauth/gsuite/login${productParam}`}
              onClick={onGoogleClick}
            >
              Log in with Google
            </Button>
            <Button
              block
              loaderOnClick
              icon={<FaGithub className="h-[1em] w-[1em]" />}
              href={`/web/oauth/github/login${productParam}`}
              onClick={onGithubClick}
            >
              Log in with Github
            </Button>
            <Button
              block
              loaderOnClick
              icon={<FaKey className="h-[1em] w-[1em]" />}
              medium
              href={`/sso-login${productParam}`}
              onClick={onSSOClick}
            >
              Log in with SSO
            </Button>
            <div className="mt-10" />
          </div>
          <Button
            medium
            block
            icon={<HiOutlineQuestionMarkCircle />}
            href="https://www.osohq.com/docs"
            onClick={onDocsClick}
          >
            Documentation
          </Button>
          <div className="mt-10" />
          <div className="text-gray-500 text-center text-xs">
            By logging into Oso Cloud, you agree to the{" "}
            <a
              href="https://www.osohq.com/user-agreement"
              className="text-blue-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              User Agreement
            </a>
            .
          </div>
          <div className="mt-10" />
        </div>
      </div>
    </PlausibleProvider>
  );
};

export default Home;
